import filter from 'lodash/filter';
import get from 'lodash/get';
import has from 'lodash/has';
import isArray from 'lodash/isArray';
import map from 'lodash/map';
import reduce from 'lodash/reduce';
import { useMemo, useState, useEffect } from 'react';

import agentsResource from 'src/actions/agent/agentsResource';
import campaignsResource from 'src/actions/campaigns/campaignsResource';
import endpointsResource from 'src/actions/endpoints/endpointsResource';
import {
  getIvrsWithLatestVersion,
  getPublishedIvrs,
} from 'src/presentation/components/pages/shared/helpers/ivrsHelper';
import Ivr from 'src/models/Ivrs/Ivr';
import ivrsResource from 'src/actions/ivrs/ivrsResource';
import recordingsResource from 'src/actions/ivrs/recordingsResource';
import useResource, { STATUS_SUCCEEDED, STATUS_LOADING } from 'src/presentation/hooks/useResource';
import { IVR_TAGS } from 'src/models/Thread/Thread';

export const useIvrsWithPhones = () => {
  const { errors, loadingState, values: ivrConfigurationsRaw } = useResource(ivrsResource);
  const {
    errors: endpointsErrors,
    values: endpoints,
    loadingState: endpointsLoadingState,
  } = useResource(endpointsResource);
  const {
    errors: campaignsErrors,
    loadingState: campaignsLoadingState,
    values: campaigns,
  } = useResource(campaignsResource);

  const { values: agents, loadingState: agentsLoadingState } = useResource(agentsResource);
  const [ivrsIdNameMap, setIvrsIdNameMap] = useState<any>();

  const voiceEndpoints = useMemo(() => {
    return filter(endpoints, value => value.type === 'VOICE');
  }, [endpoints]);

  const ivrConfigurations = useMemo(
    () =>
      filter(
        ivrConfigurationsRaw,
        (ivr: Ivr) => !ivr.tags || !ivr.tags.length || ivr.tags.includes(IVR_TAGS.TELEPHONY)
      ),
    [ivrConfigurationsRaw]
  );

  const ivrs = useMemo(() => {
    const phonesMap = reduce(
      voiceEndpoints,
      (acc: { [key: string]: any }, endpoint: any) => {
        const processIvrId = ivrId => {
          if (ivrId) {
            has(acc, ivrId) ? acc[ivrId].push(endpoint) : (acc[ivrId] = [endpoint]);
          }
        };

        processIvrId(get(endpoint, 'configuration.initialIvr.ivrId'));
        processIvrId(get(endpoint, 'configuration.queueIvr.ivrId'));

        return acc;
      },
      {}
    );

    const campaignsMap = reduce(
      campaigns,
      (acc: { [key: string]: any }, campaign: any) => {
        const ivrId = get(campaign, 'content.ivrId');
        if (ivrId) {
          has(acc, ivrId) ? acc[ivrId].push(campaign) : (acc[ivrId] = [campaign]);
        }
        return acc;
      },
      {}
    );

    const result: any = getIvrsWithLatestVersion(ivrConfigurations).map(config => {
      const configPhones = get(phonesMap, config.id);
      const isActive = has(campaignsMap, config.id) || has(phonesMap, config.id);

      const phones = isArray(configPhones) ? map(configPhones, (config: any) => config.address).join(',') : '';
      return {
        ...config,
        isActive,
        phones,
      };
    });
    return result;
  }, [campaigns, ivrConfigurations, voiceEndpoints]);

  const state =
    endpointsLoadingState === STATUS_SUCCEEDED &&
    loadingState === STATUS_SUCCEEDED &&
    campaignsLoadingState === STATUS_SUCCEEDED &&
    agentsLoadingState === STATUS_SUCCEEDED
      ? STATUS_SUCCEEDED
      : STATUS_LOADING;

  useEffect(() => {
    setIvrsIdNameMap(
      reduce(
        ivrs,
        (acc, ivr) => {
          acc[ivr.id] = { name: ivr.name || ivr.description || ivr.id, version: ivr.version };
          return acc;
        },
        {}
      )
    );
  }, [ivrs, setIvrsIdNameMap]);

  const latestOrPublishedIvrs = useMemo(() => {
    return getPublishedIvrs(ivrConfigurations);
  }, [ivrConfigurations]);

  return {
    agents,
    campaigns,
    campaignsErrors,
    endpointsErrors,
    errors,
    ivrConfigurations,
    ivrs,
    ivrsIdNameMap,
    latestOrPublishedIvrs,
    loadingState: state,
    setIvrsIdNameMap,
    voiceEndpoints,
  };
};

export const useIvrsWithPhonesAndRecordings = () => {
  const {
    agents,
    endpointsErrors,
    errors,
    ivrConfigurations,
    ivrs,
    ivrsIdNameMap,
    latestOrPublishedIvrs,
    loadingState,
    setIvrsIdNameMap,
    voiceEndpoints,
  } = useIvrsWithPhones();
  const { errors: recordingsErrors, values: recordings } = useResource(recordingsResource);

  return {
    agents,
    endpointsErrors,
    errors,
    ivrConfigurations,
    ivrs,
    ivrsIdNameMap,
    latestOrPublishedIvrs,
    loadingState,
    recordings,
    recordingsErrors,
    setIvrsIdNameMap,
    voiceEndpoints,
  };
};
